import type { BriefTranslations } from "../../types/brief";

export const brief: BriefTranslations = {
  sidebar: "Brief",
  title: "Brief",
  subtitle: "The project's brief: its files, its additions and its confirmed versions",
  loadFailed: "The brief could not be loaded",
  notAvailable: {
    title: "The brief is not available yet",
    body: "This part of Franklin is still being rolled out to your organisation. Please check back shortly.",
  },
  sections: { documents: "Documents", appendix: "Additions" },
  actions: {
    addFiles: "Add files",
    addExisting: "Add existing",
    addAppendix: "New addition",
    runCheck: "Run check now",
    confirmVersion: "Confirm version {number}",
  },
  documents: {
    hiddenCount: "Files in the brief you cannot see: {count}",
    pickerTitle: "Add files to the brief",
    pickerDescription:
      "The files you pick are copied into {folder}; the originals stay where they are.",
    pickerSubmit: "Copy into the brief",
    copied: "Copied into the brief: {count}",
    copyRefused: "Could not be copied: {count}",
    copyFailed: "The files could not be copied into the brief",
  },
  empty: {
    title: "No brief yet",
    body: "The brief is a folder of the project's requirement documents and its additions. Franklin keeps an eye on the project's progress and lets you know if conflicts with the brief are found.",
    viewerBody: "This project has no brief yet.",
    start: "Start the brief",
  },
  findings: {
    resolved: "Resolved",
    noneResolved: "Nothing resolved yet",
    suggestions: "Franklin's suggestions",
    noSuggestions: "No open suggestions",
    page: "p. {page}",
    sheetCells: "sheet {sheet} · {cells}",
    ifcElement: "element {name}",
    buildingData: "Building data",
    issueCitation: "Issue #{number}",
    accept: "Add to brief",
    acceptHint: "Adds it to the brief as an addition",
    dismiss: "Dismiss suggestion",
    reopen: "Reopen",
    status: {
      accepted: "Added as an addition",
      dismissed: "Dismissed",
      resolved: "Resolved by a brief update",
    },
    showDetails: "Show details",
    hideDetails: "Hide details",
    sections: { found: "What Franklin found", inBrief: "In the brief", evidence: "Evidence" },
    severity: { high: "High", medium: "Medium", low: "Low", info: "Info" },
    minimise: "Minimise",
    showSuggestions: "Show Franklin's suggestions (open: {count})",
    issue: {
      heading: "Suggested issue",
      marker: "Issue suggested",
      why: "Why",
      assignees: "Suggested assignees",
      noAssignees: "No one on the Contacts page fits — choose assignees when you create it.",
      disciplines: "Disciplines",
      create: "Create this issue",
      filed: "Issue #{number}",
      noLongerOnProject: "No longer on this project",
      linkFailed: "The issue was created, but it could not be linked to the suggestion",
    },
  },
  appendix: {
    placeholder: "Write an addition…",
    replyPlaceholder: "Write a reply…",
    attach: "Attach",
    send: "Send",
    reply: "Reply",
    replyingTo: "Replying to {name}",
    cancelReply: "Cancel reply",
    edit: "Edit",
    edited: "Edited",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    deleteTitle: "Delete this addition?",
    deleteBody:
      "It is removed from the brief with its replies. Confirmed versions keep their copy.",
    deleted: "Addition deleted",
    none: "No additions yet",
    franklinConfirmedBy: "Franklin · confirmed by {name}",
    briefFiles: "In the brief",
    noneInVersion: "This version has no additions.",
    createIssue: "Create issue",
    linkedIssue: "Issue #{number}",
    issueLinkFailed: "The issue was created, but it could not be linked to the addition",
  },
  issueOrigin: {
    label: "Brief addition",
    open: "Open the addition in the brief",
    suggestionLabel: "Franklin's suggestion",
    openSuggestion: "Open Franklin's suggestion in the brief",
  },
  accept: {
    title: "Add as an addition",
    body: "Edit Franklin's suggestion if you need to. It is added to the brief as an addition by Franklin, confirmed by you.",
    text: "Addition",
    attachments: "Attached documents",
    removeAttachment: "Remove {name}",
    submit: "Add to the brief",
    cancel: "Cancel",
    added: "Added to the brief as an addition",
  },
  run: {
    running: "Franklin is checking…",
    queued: "Check queued",
    trigger: {
      documentProcessed: "after an upload",
      briefChanged: "after a change to the brief",
      issueActivity: "after an issue change",
      buildingData: "after a building data change",
      manual: "started manually",
    },
    lastRun: "Last check {when}",
    failedTitle: "The last check failed",
    never: "Not checked yet",
    checkQueued: "Check queued",
    completedToast: "Franklin finished checking the brief",
    review: "Review",
    cancel: "Cancel check",
    cancelled: "Check cancelled",
    emptyBrief: "Add documents to the brief before running a check",
  },
  versions: {
    label: "Version {number}",
    current: "Current",
    unconfirmedCount: "Unconfirmed changes: {count}",
    upToDate: "Up to date",
    choose: "Versions",
    short: "V{number}",
    viewing: "Version {number}, read-only",
    confirmedBy: "{name} · {date}",
    documentCount: "Files: {count}",
    compareWithCurrent: "Compare version {number} with the current brief",
    backToCurrent: "Back to current",
    missingFiles: "Files in this version that no longer exist: {count}",
    unconfirmedTitle: "Changes since version {number}",
    unconfirmedFirst: "Not confirmed yet",
    nothingChanged: "Nothing has changed since the last confirmed version",
    delete: "Delete version {number}",
    deleteTitle: "Delete version {number}?",
    deleteBody:
      "The record of the brief as confirmed in this version is removed. Files and additions are not changed. The deletion stays in the brief's history, and the number {number} is not used again.",
    deleteConfirm: "Delete",
    cancel: "Cancel",
    deleted: "Version {number} deleted",
    gone: "This version has already been deleted",
  },
  history: {
    open: "Brief history",
    title: "History",
    empty: "No version has been confirmed yet",
    confirmed: "Version {number} confirmed",
    deleted: "Version {number} deleted",
    view: "View version {number}",
    by: "{name} · {date}",
    confirmedBy: "Confirmed by {name} · {date}",
  },
  changes: {
    added: "New",
    revised: "New version",
    moved: "Moved",
    inside: "Changes inside",
    folderAdded: "New folder",
    appendixAdded: "New",
    appendixEdited: "Edited",
    removed: "Removed: {count}",
    since: "{change} since version {number}",
  },
  diff: {
    added: "Added",
    removed: "Removed",
    revised: "New version",
    moved: "Moved",
    unchanged: "Unchanged",
    appendices: "Additions",
    appendixAdded: "New addition",
    appendixEdited: "Edited addition",
    appendixRemoved: "Removed addition",
    folders: "Folders",
    folderAdded: "New folder",
    folderRemoved: "Removed folder",
    folderRenamed: "Renamed",
    folderMoved: "Moved folder",
    compareTitle: "Compare versions",
    from: "From",
    to: "To",
    showUnchanged: "Show unchanged",
  },
  confirm: {
    title: "Confirm version {number}",
    note: "Change note",
    notePlaceholder: "What changed and why",
    submit: "Confirm",
    cancel: "Cancel",
    confirmed: "Version {number} confirmed",
    nothingChanged: "Nothing has changed since the last confirmed version",
    leaveTitle: "Confirm version {number} before you leave?",
    leaveBody:
      "You changed the brief. Your changes are saved either way; confirming records them as a new version.",
    keepWorking: "Keep working",
    leaveWithout: "Leave without confirming",
  },
  dashboardCard: {
    goToBrief: "Go to brief",
    moreSuggestions: "+{count} more",
  },
};
